import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Plus, Trash2, Pencil, Save, X, ArrowLeft, Clock, PauseCircle,
  Zap, ZapOff, Calendar, Building2, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useSettings } from '../context/SettingsContext';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/dateFormat';

// ---------- Time helpers ----------------------------------------------------

/**
 * Convert an ISO string → `"YYYY-MM-DDTHH:mm"` (the value shape the
 * native `<input type="datetime-local">` expects, in the user's LOCAL
 * timezone). Returns empty string when passed anything unparseable.
 */
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * `<input datetime-local>` returns a naïve `"YYYY-MM-DDTHH:mm"`. Treat
 * it as LOCAL time (which is what the browser does) and convert back
 * to an ISO-8601 UTC string for the API.
 */
const localInputToIso = (val) => (val ? new Date(val).toISOString() : '');

const fmtDT = (iso) => formatDateTime(iso);

// ---------- Modal editor ----------------------------------------------------

const EditorModal = ({ open, initial, branches, onSave, onClose, saving }) => {
  const [label, setLabel] = useState('');
  const [fromLocal, setFromLocal] = useState('');
  const [toLocal, setToLocal] = useState('');
  const [scope, setScope] = useState('all');
  const [branchIds, setBranchIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLabel(initial?.label || '');
    setFromLocal(isoToLocalInput(initial?.from_ts));
    setToLocal(isoToLocalInput(initial?.to_ts));
    setScope(initial?.scope || 'all');
    setBranchIds(initial?.branch_ids || []);
  }, [open, initial]);

  if (!open) return null;

  const toggleBranch = (id) => {
    setBranchIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const canSave = fromLocal && toLocal && localInputToIso(toLocal) > localInputToIso(fromLocal)
    && (scope === 'all' || branchIds.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="cutoff-editor-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">
              {initial ? 'Edit cut-off period' : 'New cut-off period'}
            </h3>
            <p className="text-xs text-neutral-500 mt-0.5">Blocks branch order-placement between the two timestamps below.</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-100" data-testid="cutoff-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          <div>
            <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Q1 Stock Take"
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0078D4] focus:ring-1 focus:ring-[#0078D4]"
              data-testid="cutoff-editor-label"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5">
                From — start of freeze
              </label>
              <input
                type="datetime-local"
                value={fromLocal}
                onChange={(e) => setFromLocal(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0078D4] focus:ring-1 focus:ring-[#0078D4]"
                data-testid="cutoff-editor-from"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-1.5">
                To — orders reopen
              </label>
              <input
                type="datetime-local"
                value={toLocal}
                onChange={(e) => setToLocal(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0078D4] focus:ring-1 focus:ring-[#0078D4]"
                data-testid="cutoff-editor-to"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 uppercase tracking-wide mb-2">Apply to</label>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => { setScope('all'); setBranchIds([]); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                  scope === 'all' ? 'bg-[#0078D4] text-white border-[#0078D4]' : 'bg-white text-neutral-600 border-neutral-300 hover:border-[#0078D4]'
                }`}
                data-testid="cutoff-editor-scope-all"
              >
                All branches
              </button>
              <button
                type="button"
                onClick={() => setScope('branches')}
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                  scope === 'branches' ? 'bg-[#0078D4] text-white border-[#0078D4]' : 'bg-white text-neutral-600 border-neutral-300 hover:border-[#0078D4]'
                }`}
                data-testid="cutoff-editor-scope-branches"
              >
                Selected branches only
              </button>
            </div>
            {scope === 'branches' && (
              <div className="rounded-xl border border-neutral-200 max-h-56 overflow-y-auto" data-testid="cutoff-editor-branch-list">
                {branches.length === 0 && (
                  <div className="p-4 text-xs text-neutral-500 italic">No branches configured — visit Admin → Branches to add one first.</div>
                )}
                {branches.map((b) => {
                  const checked = branchIds.includes(b.id);
                  return (
                    <label
                      key={b.id}
                      className={`flex items-center gap-3 px-3 py-2 border-b last:border-b-0 border-neutral-100 cursor-pointer hover:bg-neutral-50 ${checked ? 'bg-blue-50' : ''}`}
                      data-testid={`cutoff-editor-branch-${b.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleBranch(b.id)}
                        className="w-4 h-4"
                      />
                      <Building2 className="w-4 h-4 text-neutral-400" />
                      <div>
                        <div className="text-sm font-medium text-[#0F172A]">{b.name}</div>
                        {b.customer_numbers?.length > 0 && (
                          <div className="text-[10px] text-neutral-500">
                            {b.customer_numbers.slice(0, 3).join(', ')}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-semibold text-neutral-700 hover:bg-neutral-200"
            disabled={saving}
            data-testid="cutoff-editor-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({
              label,
              from_ts: localInputToIso(fromLocal),
              to_ts: localInputToIso(toLocal),
              scope,
              branch_ids: scope === 'branches' ? branchIds : [],
            })}
            disabled={!canSave || saving}
            className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-[#0078D4] hover:bg-[#106EBE] disabled:bg-neutral-300 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="cutoff-editor-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save period'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Page -----------------------------------------------------------

const AdminCutoffSettings = () => {
  const { authHeaders } = useAdminAuth();
  const { settings } = useSettings();
  const { toast } = useToast();

  const [enabled, setEnabled] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { document.title = 'Cut-off Schedule — Admin'; }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([
        axios.get(`${API}/admin/cutoff/periods`, { headers: authHeaders }),
        axios.get(`${API}/admin/branches`, { headers: authHeaders }).catch(() => ({ data: { branches: [] } })),
      ]);
      setPeriods(p.data.periods || []);
      setEnabled(p.data.enabled !== false);
      setBranches(b.data.branches || b.data || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next); // optimistic
    try {
      await axios.post(`${API}/admin/cutoff/status`, { enabled: next }, { headers: authHeaders });
      toast({
        title: next ? 'Cut-off feature enabled' : 'Cut-off feature disabled',
        description: next ? 'Configured windows are now active.' : 'All windows are ignored until you re-enable this.',
      });
    } catch (e) {
      setEnabled(!next); // revert
      toast({ title: 'Toggle failed', description: e?.response?.data?.detail || e?.message });
    }
  };

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (p) => { setEditing(p); setEditorOpen(true); };

  const savePeriod = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/admin/cutoff/periods/${editing.id}`, payload, { headers: authHeaders });
        toast({ title: 'Cut-off period updated' });
      } else {
        await axios.post(`${API}/admin/cutoff/periods`, payload, { headers: authHeaders });
        toast({ title: 'Cut-off period created' });
      }
      setEditorOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  const removePeriod = async (p) => {
    if (!window.confirm(`Delete cut-off period ${p.label ? `"${p.label}"` : ''}?`)) return;
    try {
      await axios.delete(`${API}/admin/cutoff/periods/${p.id}`, { headers: authHeaders });
      toast({ title: 'Cut-off period deleted' });
      setPeriods((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e?.message });
    }
  };

  const branchNameById = useMemo(() => {
    const m = {};
    for (const b of branches) m[b.id] = b.name;
    return m;
  }, [branches]);

  const now = Date.now();
  const isPeriodActive = (p) => {
    const a = new Date(p.from_ts).getTime();
    const b = new Date(p.to_ts).getTime();
    return enabled && a <= now && now < b;
  };
  const isPeriodPast = (p) => new Date(p.to_ts).getTime() <= now;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header — company logo + back link + title */}
        <div className="rounded-3xl bg-gradient-to-br from-[#0F172A] via-[#1E3A5F] to-[#0F172A] px-6 sm:px-10 py-8 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-blue-400/20 blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
          <div className="relative">
            <Link
              to="/admin/settings"
              className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white mb-4 transition"
              data-testid="cutoff-back-to-settings"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Settings
            </Link>
            <div className="flex items-center gap-4 flex-wrap">
              {settings?.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt="Company logo"
                  className="h-14 sm:h-16 object-contain drop-shadow-lg"
                  data-testid="cutoff-header-logo"
                />
              ) : (
                <div className="text-white font-black text-lg sm:text-xl tracking-wide">MOHAMMED RIAZ &amp; PARTNER LLC</div>
              )}
              <div>
                <h1 className="text-white text-2xl sm:text-3xl font-black leading-tight">Cut-off Schedule</h1>
                <p className="text-white/70 text-sm mt-1 max-w-xl">
                  Pause branch orders for stock-takes, freezes, or maintenance. Each window has its own start &amp; end and applies to all or specific branches.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Global toggle card */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-5 sm:p-6 flex items-center gap-4 flex-wrap" data-testid="cutoff-global-toggle-card">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}`}>
            {enabled ? <Zap className="w-6 h-6" /> : <ZapOff className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-bold text-[#0F172A]">
              Cut-off enforcement is {enabled ? 'ON' : 'OFF'}
            </div>
            <div className="text-xs text-neutral-500 mt-0.5">
              {enabled
                ? 'Configured windows will block affected branches from placing orders.'
                : 'All windows below are ignored — branches can order at any time.'}
            </div>
          </div>
          <button
            onClick={toggleEnabled}
            className={`relative inline-flex items-center h-8 w-14 rounded-full transition ${enabled ? 'bg-emerald-500' : 'bg-neutral-300'}`}
            data-testid="cutoff-global-toggle-btn"
            aria-pressed={enabled}
          >
            <span className={`inline-block w-6 h-6 rounded-full bg-white shadow transform transition ${enabled ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Periods */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-bold text-[#0F172A]">Cut-off windows</h2>
              <p className="text-xs text-neutral-500 mt-0.5">{periods.length} configured</p>
            </div>
            <button
              onClick={openNew}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white bg-[#0078D4] hover:bg-[#106EBE]"
              data-testid="cutoff-add-btn"
            >
              <Plus className="w-4 h-4" /> Add period
            </button>
          </div>

          {loading ? (
            <div className="py-12 flex justify-center text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : periods.length === 0 ? (
            <div className="py-16 text-center text-sm text-neutral-500 border-2 border-dashed border-neutral-200 rounded-xl">
              <PauseCircle className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
              <div className="font-semibold text-neutral-700 mb-1">No cut-off periods yet</div>
              <div className="text-xs italic">Add one to freeze orders during stock-takes or maintenance.</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="cutoff-periods-list">
              {periods.map((p) => {
                const active = isPeriodActive(p);
                const past = isPeriodPast(p);
                return (
                  <div
                    key={p.id}
                    className={`rounded-2xl border p-5 relative ${
                      active ? 'border-amber-300 bg-amber-50/40' : past ? 'border-neutral-200 bg-neutral-50/60 opacity-70' : 'border-neutral-200 bg-white'
                    }`}
                    data-testid={`cutoff-period-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1.5">
                          {active && (
                            <span className="inline-flex items-center gap-1 text-amber-800 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`cutoff-period-active-${p.id}`}>
                              <PauseCircle className="w-3 h-3" /> Active now
                            </span>
                          )}
                          {past && !active && (
                            <span className="inline-flex items-center gap-1 text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5">
                              <CheckCircle2 className="w-3 h-3" /> Past
                            </span>
                          )}
                          {!active && !past && (
                            <span className="inline-flex items-center gap-1 text-blue-700 bg-blue-100 rounded-full px-2 py-0.5">
                              <Clock className="w-3 h-3" /> Upcoming
                            </span>
                          )}
                        </div>
                        <div className="font-bold text-[#0F172A] truncate">
                          {p.label || 'Cut-off period'}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => openEdit(p)}
                          className="p-2 rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-[#0078D4]"
                          data-testid={`cutoff-period-edit-${p.id}`}
                          aria-label="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removePeriod(p)}
                          className="p-2 rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600"
                          data-testid={`cutoff-period-delete-${p.id}`}
                          aria-label="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="text-xs text-neutral-500 flex items-start gap-1.5 mb-1">
                      <Calendar className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div><span className="text-neutral-700 font-medium">From:</span> {fmtDT(p.from_ts)}</div>
                        <div><span className="text-neutral-700 font-medium">To:</span> {fmtDT(p.to_ts)}</div>
                      </div>
                    </div>

                    <div className="text-xs text-neutral-500 flex items-center gap-1.5 mt-2">
                      <Building2 className="w-3.5 h-3.5 shrink-0" />
                      {p.scope === 'all' ? (
                        <span className="text-neutral-700">All branches</span>
                      ) : (
                        <span className="truncate">
                          {(p.branch_ids || []).map((id) => branchNameById[id] || id.slice(0, 6)).join(', ')}
                        </span>
                      )}
                    </div>

                    {!enabled && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-neutral-500 italic">
                        <AlertCircle className="w-3 h-3" /> Global toggle is OFF — this window won&rsquo;t fire.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <EditorModal
        open={editorOpen}
        initial={editing}
        branches={branches}
        onSave={savePeriod}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
        saving={saving}
      />
    </AdminLayout>
  );
};

export default AdminCutoffSettings;
